import { map } from 'rxjs/operators'
import { Injectable } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { AccountService } from '@services/account.service'
import { BaseResponse } from '@services/api/api-base.service'
import { NoticeSessionMessage } from '@models/planning/notice-session-message.model'
import { ManagementApiBaseService } from '@services/api/management-api-base.service'

@Injectable({
	providedIn: 'root'
})
export class NoticeSessionService extends ManagementApiBaseService<any> {
	private readonly source: string

	constructor(
		override http: HttpClient,
		private account: AccountService
	) {
		super(http)
		this.source = 'notice-sessions'
	}

	/** `openingJustification`: exigida pelo servidor quando a primeira abertura passa da tolerância do horário do edital (PC-02). */
	opened(id: string, loose: boolean, openingJustification?: string) {
		return this.patch<any>(`${this.source}/opened`, { noticeSession: { id }, loose, openingJustification })
	}

	closed(id: string, loose: boolean) {
		return this.patch<any>(`${this.source}/closed`, { noticeSession: { id }, loose })
	}

	/** Encerramento forçado (spec 024) — fura-fluxo com justificativa, disponível para toda modalidade. */
	forcedClosed(id: string, justification: string) {
		return this.patch<any>(`${this.source}/forced-closed`, { noticeSession: { id }, justification })
	}

	locked(id: string, locked: boolean) {
		return this.patch<any>(`${this.source}/locked`, { noticeSession: { id }, locked })
	}

	/** Define/reajusta o prazo de recurso da sessão (spec 023) — só com a sessão já fechada normalmente. */
	appealExpiration(id: string, appealExpiresAt: string) {
		return this.patch<any>(`${this.source}/appeal-expiration`, { noticeSession: { id }, appealExpiresAt })
	}

	publicProfile(id: string, publicProfile: boolean) {
		return this.patch<any>(`${this.source}/public-profile`, { noticeSession: { id }, publicProfile })
	}

	/**
	 * Adendo ao edital (spec 031): republicação para a disputa — só com o edital publicado e a
	 * sessão ainda CREATED. Remove as propostas iniciais da sessão; o documento do adendo entra
	 * pelos complementares da demanda e a publicação no PNCP como "outros" é uma chamada à parte.
	 */
	addendum(id: string, justification: string) {
		return this.patch<any>(`${this.source}/addendum`, { noticeSession: { id }, justification })
	}

	republishContest(
		id: string,
		openedDate: string,
		openedTime: string,
		closedDate: string,
		closedTime: string,
		bidTimerMinutes?: number,
		minIntervalBetweenBids?: number
	) {
		return this.patch<any>(`${this.source}/republish-contest`, {
			noticeSession: { id },
			openedDate,
			openedTime,
			closedDate,
			closedTime,
			bidTimerMinutes,
			minIntervalBetweenBids
		})
	}

	sendMessage(entity: NoticeSessionMessage) {
		return this.post<NoticeSessionMessage, BaseResponse>(`${this.source}/messages`, entity)
	}

	deleteMessage(id: string) {
		return this.delete<BaseResponse>(`${this.source}/messages/${id}`)
	}

	messages(id: string, tenantId: string) {
		return this.get<Array<NoticeSessionMessage>>(`${this.source}/messages/${id}/${tenantId}`).pipe(
			map(response =>
				response.map(it => {
					const entity = new NoticeSessionMessage(it)
					entity.myRequest = this.account.user.id === it.createdBy
					return entity
				})
			)
		)
	}
}
